/**
 * ReportSheet — segnalare un puzzle ricevuto, o bloccare chi l'ha mandato (#65).
 *
 * Apple (1.2) e Google Play chiedono che chi riceve contenuti generati da altri
 * possa segnalarli e bloccare chi li manda **dentro l'app**. Le due azioni sono
 * separate: si può segnalare senza bloccare, e bloccare senza segnalare.
 *
 * Props:
 *   open       {boolean}
 *   onClose    {function}
 *   onReport   {({reason, note}) => Promise<{already: boolean}>}
 *   onBlock    {function|null}  — null se non c'è nessuno da bloccare
 *                                  (puzzle vecchi senza creatore, o il tuo)
 */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MOTIVI } from "../services/reportService";

export default function ReportSheet({ open, onClose, onReport, onBlock }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [stato, setStato] = useState("idle"); // idle | sending | sent | already | error | blockError

  // Si riparte da zero solo quando il foglio si apre. Dipendere anche da
  // `onClose` — una funzione nuova a ogni render del genitore — azzerava motivo
  // e nota a ogni ridisegno dell'app, per esempio quando arriva il profilo.
  useEffect(() => {
    if (!open) return;
    setReason(null);
    setNote("");
    setStato("idle");
  }, [open]);

  const chiudiRef = useRef(onClose);
  chiudiRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => e.key === "Escape" && chiudiRef.current && chiudiRef.current();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);

  if (!open) return null;

  const invia = async () => {
    if (!reason || stato === "sending") return;
    setStato("sending");
    try {
      const esito = await onReport({ reason, note });
      setStato(esito && esito.already ? "already" : "sent");
    } catch (_) {
      setStato("error");
    }
  };

  const blocca = async () => {
    try {
      await onBlock();
    } catch (_) {
      setStato("blockError");
    }
  };

  const fatto = stato === "sent" || stato === "already";

  return (
    <div onClick={onClose} style={overlay}>
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-sheet-title"
        style={card}
      >
        <h2 id="report-sheet-title" style={title}>{t("ui.moderation.sheetTitle")}</h2>

        {fatto ? (
          <p style={lead} data-testid="report-done" role="status">
            {t(stato === "already" ? "ui.moderation.alreadySent" : "ui.moderation.sent")}
          </p>
        ) : (
          <>
            <p style={lead}>{t("ui.moderation.sheetLead")}</p>
            <div role="radiogroup" aria-label={t("ui.moderation.reasonsAria")} style={{ display: "grid", gap: 8, marginBottom: 12 }}>
              {MOTIVI.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={reason === m}
                  onClick={() => setReason(m)}
                  style={reason === m ? { ...opzione, ...opzioneScelta } : opzione}
                >
                  {t(`ui.moderation.reasons.${m}`)}
                </button>
              ))}
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder={t("ui.moderation.notePlaceholder")}
              rows={2}
              style={nota}
            />
            {stato === "error" && <p style={errore} role="alert">{t("ui.moderation.sendError")}</p>}
            <button
              type="button"
              onClick={invia}
              disabled={!reason || stato === "sending"}
              style={{ ...primario, opacity: !reason || stato === "sending" ? 0.5 : 1 }}
              data-testid="report-send"
            >
              {t(stato === "sending" ? "ui.moderation.sending" : "ui.moderation.send")}
            </button>
          </>
        )}

        {onBlock && (
          <button type="button" onClick={blocca} style={secondario} data-testid="report-block">
            {t("ui.moderation.block")}
          </button>
        )}
        {stato === "blockError" && <p style={errore} role="alert">{t("ui.moderation.blockError")}</p>}

        <button type="button" onClick={onClose} style={{ ...secondario, background: "transparent" }}>
          {t(fatto ? "ui.moderation.close" : "ui.moderation.cancel")}
        </button>
        <p style={contatto}>{t("ui.moderation.contact")}</p>
      </div>
    </div>
  );
}

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  zIndex: 1300, // sopra la testata col logo (1200)
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
};
const card = {
  background: "var(--color-surface, #fff)",
  borderRadius: "2rem 2rem 0 0",
  padding: "28px 24px calc(20px + env(safe-area-inset-bottom, 0px))",
  maxWidth: 480,
  width: "100%",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 -16px 48px rgba(0,0,0,0.18)",
};
const title = {
  fontFamily: "var(--font-display)",
  fontSize: 20,
  fontWeight: 800,
  color: "var(--color-text)",
  margin: "0 0 8px",
};
const lead = {
  fontFamily: "var(--font-body)",
  fontSize: 14,
  lineHeight: 1.5,
  color: "var(--color-textMuted)",
  margin: "0 0 16px",
};
const opzione = {
  textAlign: "left",
  padding: "12px 16px",
  borderRadius: 14,
  border: "none",
  background: "var(--color-bgAlt, #f5eef1)",
  color: "var(--color-text)",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  cursor: "pointer",
};
const opzioneScelta = {
  background: "var(--color-primary, #a8275a)",
  color: "#fff",
  fontWeight: 700,
};
const nota = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  borderRadius: 14,
  border: "none",
  background: "var(--color-bgAlt, #f5eef1)",
  fontFamily: "var(--font-body)",
  fontSize: 14,
  color: "var(--color-text)",
  resize: "vertical",
  marginBottom: 12,
};
const primario = {
  width: "100%",
  padding: "14px 20px",
  border: "none",
  borderRadius: 16,
  fontFamily: "var(--font-body)",
  fontSize: 16,
  fontWeight: 700,
  background: "linear-gradient(135deg, var(--color-primary, #a8275a), var(--color-primaryBright, #ff709f))",
  color: "#fff",
  cursor: "pointer",
  marginBottom: 8,
};
const secondario = {
  width: "100%",
  padding: "12px 20px",
  border: "none",
  borderRadius: 16,
  fontFamily: "var(--font-body)",
  fontSize: 15,
  fontWeight: 600,
  background: "var(--color-bgAlt, #f5eef1)",
  color: "var(--color-text)",
  cursor: "pointer",
  marginBottom: 8,
};
const errore = {
  fontFamily: "var(--font-body)",
  fontSize: 13,
  color: "var(--color-wrong, #c0392b)",
  margin: "0 0 10px",
};
const contatto = {
  fontFamily: "var(--font-body)",
  fontSize: 12,
  color: "var(--color-textDim, #8a7d83)",
  textAlign: "center",
  margin: "6px 0 0",
};
